import { Group, Stack } from "@mantine/core"
import { keepPreviousData } from "@tanstack/react-query"
import { RowSelectionState } from "@tanstack/react-table"
import { ColumnManager, Loader } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { CustomTable } from "~/components/table/CustomTable"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindAgecComplianceQueryResponse,
  ProductFilterAndSearchRequestDto,
  useFindAgecCompliance,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { AgecBulkActions } from "~/pages/Brand/Regulations/AGEC/AgecBulkActions.tsx"
import { useAgecTable } from "~/pages/Brand/Regulations/AGEC/useAgecTable"

export const ProductsAGECTable = () => {
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { t } = useTranslation("regulations")
  const { t: tProducts } = useTranslation("products")
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<ProductFilterAndSearchRequestDto>([
      {
        label: t("lists.placeholders.agec"),
        key: "agecCompliance",
        type: "multiSelect",
        options: [
          { value: "yes", label: t("lists.placeholders.yes") },
          { value: "no", label: t("lists.placeholders.no") },
          {
            value: "not-applicable",
            label: tProducts("lists.cells.agec.notApplicable"),
          },
        ],
      },
    ])

  const {
    data: productsData,
    isPending: isLoadingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindAgecCompliance,
    FindAgecComplianceQueryResponse
  >(
    useFindAgecCompliance,
    {
      filters: filtersValue,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    }
  )

  const table = useAgecTable({
    rowSelection,
    pagination: pageBasedPagination,
    setPagination: setPageBasedPagination,
    setRowSelection,
    pageCount: totalPages,
    products: productsData?.data ?? [],
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.AGEC} />
      <Group>
        <SearchBar
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          placeholder={tProducts("lists.placeholders.searchProduct")}
          isFetching={isLoadingProducts}
        />
        <FilterBuilderWrapper<ProductFilterAndSearchRequestDto>
          title={title}
          searchPlaceholder={searchPlaceholder}
          options={options}
          filters={filtersValue}
          setFilters={setFiltersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <AgecBulkActions table={table} />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.AGEC} />
      </Group>
      {!isLoadingProducts ? (
        <CustomTable
          table={table}
          pagination={true}
          manualPaginationCount={productsData?.count}
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
          stickyHeader
        />
      ) : (
        <Loader fullPage />
      )}
    </Stack>
  )
}
